import { Box } from "@mui/material";
import * as React from "react";
import dayjs from "dayjs";
import AntDatePicker from "../../Helper/DatePicker/AntDatePicker";
import { getDatePickerDate } from "../../Helper/DatePicker/DateConverter";
import { rangePresetsForDigitalTwin } from "../../Static/Data/Analytics/Analytics";
import CustomIcon from "../../Components/CustomIcon";


const defaultStartDate = getDatePickerDate(undefined, 0)
const defaultEndDate = getDatePickerDate(undefined, 0)

const ChartCalendar = ({
    startDate = defaultStartDate,
    endDate = defaultEndDate,
    onDateChange = () => { },
    onMaxRangeExceeded = null, // called with { adjustedEndDate } when selection exceeds maxRangeDays
    dataGapDates = [],
    isDisabled = false,
    disableBeforeDate = '', // YYYY-MM-DD format
    disableAfterDate = '', // YYYY-MM-DD format
    forcePresets = false,
    disablePresets = false, // Set to true to disable presets completely
    maxRangeDays = null, // Maximum number of days allowed in range (e.g., 7)
    resetTo = null, // { startDate: 'YYYY-MM-DD', endDate: 'YYYY-MM-DD' } — overrides Reset preset dates
}) => {

    const [open, setOpen] = React.useState(false);
    const [isCalendarHover, setIsCalendarHover] = React.useState(false);
    const [currentDates, setCurrentDates] = React.useState({
        startDate,
        endDate
    });
    const [rangePresets, setRangePresets] = React.useState([]);
    const [selectedStartDate, setSelectedStartDate] = React.useState(null);

    const handleDateChange = (startDate, endDate) => {
        if (startDate > endDate) {
            return false
        }

        // Validate maxRangeDays if set
        if (maxRangeDays) {
            const start = dayjs(startDate);
            const end = dayjs(endDate);
            const daysDiff = end.diff(start, 'days');

            if (daysDiff > maxRangeDays - 1) {
                // Adjust end date to be within the allowed range
                const adjustedEndDate = start.add(maxRangeDays - 1, 'days').format('YYYY-MM-DD');
                let dateObj = { startDate, endDate: adjustedEndDate }
                setCurrentDates(dateObj)
                onDateChange(dateObj)
                if (onMaxRangeExceeded) {
                    onMaxRangeExceeded({ adjustedEndDate })
                }
                return false
            }
        }

        let dateObj = { startDate, endDate }
        setCurrentDates(dateObj)
        onDateChange(dateObj)
    }

    const handleCalendarOpen = (open) => {
        if (!open) { setIsCalendarHover(false); }
        setOpen(open)
    }

    const updateRangePresets = (updatedValues) => {
        let presetsToUse = updatedValues;

        // Filter presets based on maxRangeDays if set
        if (maxRangeDays) {
            presetsToUse = updatedValues.filter((preset) => {
                if (preset.value && preset.value[0] && preset.value[1]) {
                    const start = dayjs(preset.value[0]);
                    const end = dayjs(preset.value[1]);
                    const daysDiff = end.diff(start, 'day') + 1; // +1 to include both start and end days
                    return daysDiff <= maxRangeDays;
                }
                return true;
            });
        }

        const resetStart = resetTo ? dayjs(resetTo.startDate) : dayjs(defaultStartDate);
        const resetEnd = resetTo ? dayjs(resetTo.endDate) : dayjs(defaultEndDate);
        setRangePresets([
            ...presetsToUse,
            {
                label: "Reset",
                value: [resetStart, resetEnd]
            }
        ]);
    }

    React.useEffect(() => {
        setCurrentDates({ startDate, endDate });
    }, [startDate, endDate]);

    React.useEffect(() => {
        if (disablePresets) {
            // Disable presets completely
            setRangePresets([]);
        } else if (disableBeforeDate) {
            const filteredPresets = rangePresetsForDigitalTwin.filter((range) => {
                const addedOnDate = new Date(disableBeforeDate);
                const startDate = new Date(range.value[0]);
                return addedOnDate <= startDate;
            });
            updateRangePresets(filteredPresets);
        } else if (forcePresets) {
            // Initialize presets when disableBeforeDate is not set
            updateRangePresets(rangePresetsForDigitalTwin);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disableBeforeDate, disablePresets, forcePresets, maxRangeDays, resetTo])

    return (
        <Box sx={{
            'display': 'flex',
            'alignItems': 'center',
            'justifyContent': 'center',
        }}>
            <AntDatePicker
                isDisabled={isDisabled}
                page={'digitalTwin'}
                popupClassName="analytics-calendar-popup"
                changeTheDate={handleDateChange}
                date={[currentDates.startDate, currentDates.endDate]}
                allowClear={true}
                onOpenChange={handleCalendarOpen}
                onMouseEnter={() => setIsCalendarHover(true)}
                onMouseLeave={() => setIsCalendarHover(false)}
                dataGapDates={dataGapDates}
                rangePresets={rangePresets}
                prefix={<CustomIcon stroke={(open || isCalendarHover) && !isDisabled ? "#6D72F6" : "#444B51"} name="calendar" />}
                suffixIcon={<CustomIcon sx={{ fontSize: '11px' }} stroke={(open || isCalendarHover) && !isDisabled ? "#6D72F6" : "#444B51"} name="downArrow" />}
                onCalendarChange={(dates) => {
                    // Track the first selected date to enable dynamic range limiting
                    if (dates && dates[0] && !dates[1]) {
                        setSelectedStartDate(dates[0]);
                    } else if (!dates || (dates[0] && dates[1])) {
                        setSelectedStartDate(null);
                    }
                }}
                disabledDate={(current) => {
                    // Disable dates before the specified minimum date
                    if (disableBeforeDate && current < dayjs(disableBeforeDate).startOf("day")) {
                        return true;
                    }

                    // Disable dates after the specified maximum date
                    if (disableAfterDate && current > dayjs(disableAfterDate).endOf("day")) {
                        return true;
                    }

                    // If maxRangeDays is set and user has selected a start date, disable dates outside the allowed range
                    if (maxRangeDays && selectedStartDate) {
                        const minSelectableDate = selectedStartDate.subtract(maxRangeDays - 1, 'day');
                        const maxSelectableDate = selectedStartDate.add(maxRangeDays - 1, 'day');

                        // Disable if outside the range
                        if (current < minSelectableDate.startOf('day') || current > maxSelectableDate.endOf('day')) {
                            return true;
                        }
                    }

                    return false;
                }}
            />
        </Box>
    )
}


export default ChartCalendar;
